import React, {
  ReactElement,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { animated, useSpring } from "react-spring";
import styled from "styled-components";
import Flex from "./Flex";

type TabsProps = {
  children: ReactElement[];
  size?: "small" | "normal";
};

type TabButtonProps = {
  active: boolean;
  size: "normal" | "small";
};

export const TabButton = styled.button<TabButtonProps>`
  padding: ${(props) => (props.size === "normal" ? 12 : 6)}px;
  padding-left: 18px;
  padding-right: 18px;
  cursor: pointer;
  background: none;
  border: 0;
  font-weight: 600;
  transition: color 0.3s ease;
  font-size: ${(props) => (props.size === "normal" ? 16 : 12)}px;
  color: ${(props) =>
    props.active ? props.theme.colors.black : props.theme.colors.grey2};

  &:hover {
    color: ${(props) => props.theme.colors.black};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.black};
    outline-offset: -2px;
  }
`;

const TabListContainer = styled.div`
  position: relative;
  display: flex;
  margin-bottom: 24px;
  flex-direction: row;
  max-width: 100%;
  overflow-x: auto;
  padding-bottom: 2px;

  &:after {
    display: block;
    height: 2px;
    width: 100%;
    background-color: ${(props) => props.theme.colors.grey3};
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
  }
`;

const ActiveMarker = styled(animated.div)`
  height: 2px;
  background-color: ${(props) => props.theme.colors.primary};
  position: absolute;
  bottom: 0px;
  z-index: 1;
`;

export type TabListProps = {
  /** Tab labels. Plain strings, or richer content such as a label plus a count. */
  options: ReactNode[];
  activeIndex: number;
  onSelect: (tabIndex: number) => void;
  spread?: boolean;
  size?: "small" | "normal";
  /** Accessible name for the tab list, e.g. "Filter your to-do list". */
  ariaLabel?: string;
  /** Gives each tab the id `${idPrefix}-${index}` so a panel can point at it. */
  idPrefix?: string;
  /** Id of the panel these tabs control. */
  panelId?: string;
  className?: string;
};

export const TabList = ({
  options,
  activeIndex,
  onSelect,
  spread,
  size = "normal",
  ariaLabel,
  idPrefix,
  panelId,
  className,
}: TabListProps) => {
  const buttons = useRef<HTMLButtonElement[]>([]);
  const [pos, setPos] = useState<[number, number]>([0, 0]);

  const style = useSpring({
    left: pos[0],
    width: pos[1],
  });

  // Measure after every render: labels (and so tab widths) can change
  // without the active tab changing, e.g. when a count updates.
  useLayoutEffect(() => {
    const item = buttons.current[activeIndex];
    const next: [number, number] = [
      item?.offsetLeft ?? 0,
      item?.offsetWidth ?? 0,
    ];
    if (next[0] !== pos[0] || next[1] !== pos[1]) {
      setPos(next);
    }
  });

  const handleKeyDown = (index: number) => (e: React.KeyboardEvent) => {
    const last = options.length - 1;
    const target =
      e.key === "ArrowRight"
        ? index === last
          ? 0
          : index + 1
        : e.key === "ArrowLeft"
          ? index === 0
            ? last
            : index - 1
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? last
              : null;

    if (target != null) {
      e.preventDefault();
      onSelect(target);
      buttons.current[target]?.focus();
    }
  };

  return (
    <TabListContainer
      role="tablist"
      aria-label={ariaLabel}
      className={className}
    >
      {options.map((x, i) => (
        <TabButton
          key={i}
          ref={(el) => (buttons.current[i] = el!)}
          type="button"
          role="tab"
          id={idPrefix != null ? `${idPrefix}-${i}` : undefined}
          aria-selected={i === activeIndex}
          aria-controls={panelId}
          active={i === activeIndex}
          onClick={() => onSelect(i)}
          tabIndex={i === activeIndex ? 0 : -1}
          onKeyDown={handleKeyDown(i)}
          size={size}
          style={{ flex: spread ? 1 : undefined }}
        >
          {x}
        </TabButton>
      ))}
      <ActiveMarker style={style} aria-hidden="true" />
    </TabListContainer>
  );
};

export const TabPane = styled.div`
  width: 100%;
`;

export const Tabs = ({ children, size = "normal" }: TabsProps) => {
  const [selectedTab, setSelectedTab] = useState(0);

  return (
    <>
      <TabList
        options={children
          .filter((child) => React.isValidElement(child))
          .map((x) => x.props.defaultValue)}
        activeIndex={selectedTab}
        onSelect={setSelectedTab}
        size={size}
      />
      <Flex>
        {children.filter((child) => React.isValidElement(child))[selectedTab]}
      </Flex>
    </>
  );
};
